import {
  TextField,
  FunctionField,
  BulkExportButton,
  BulkDeleteButton,
  TopToolbar,
  CreateButton,
  WrapperField,
  ReferenceField,
  SelectColumnsButton,
  useListController,
  useDataProvider,
  useNotify,
  ListContextProvider,
} from "react-admin";
import { ChunkedExportButton } from "@/components/common/ChunkedExportButton";
import { Download as DownloadIcon } from "@mui/icons-material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { Button } from "@mui/material";
import { ManageFamilyDialog } from "./components/ManageFamilyDialog";
import { MergeSelectedClaimantsButton } from "./components/MergeSelectedClaimantsButton";
import { PolicyFieldFromClaimant } from "./components/PolicyFieldFromClaimant";
import { ClaimCountField } from "./components/ClaimCountField";
import { getProps } from "@/utils/helpers/columnsHelper";
import {
  AggregateDeductibleCell,
  SpecificDeductibleCell,
  TotalPaidCell,
  SpendOverDeductibleCell,
  OpenTasksCell,
  WorkStatusCell,
} from "./components/ClaimantListCells";
import { CreateInDialog } from "@/components/common/CreateInDialog";
import { useState } from "react";
import {
  ResourceName,
  type Claimant,
  type ClaimantCreate,
  type ContractLaserRequest,
  type LaseredConditionRequest,
  type ValidAny,
} from "@/types";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import { laseredConditionApi } from "@/resources/policies/hooks/useLaseredConditions";
import { ClaimantListAside } from "./components/ClaimantListAside";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { PageHeader } from "@/components/common/PageHeader";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { getResourceIcon } from "@/utils/icons";
import { jsonExporter } from "@/utils/exporters/jsonExporter";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { isNonProductionEnvironment } from "@/utils/environment";

export const ClaimantList = () => {
  // Explicit default sort preserves the prior server order now that the API
  // honors sort. Without it react-admin would default to id ASC.
  const listContext = useListController({
    resource: ResourceName.CLAIMANTS,
    sort: { field: "claimant_name", order: "ASC" },
  });
  const { isPending: loading } = listContext;
  const ClaimantsIcon = getResourceIcon(ResourceName.CLAIMANTS);

  return (
    <>
      <ListContextProvider value={listContext}>
        <PageHeader
          customTitle="Claimants"
          hideSectionTitle
          rightContent={<ClaimantsListActions loading={loading} />}
          headerIcon={<ClaimantsIcon sx={{ fontSize: 28, color: "primary.main" }} />}
        />
        <List
          actions={false}
          aside={<ClaimantListAside />}
          perPage={25}
          sort={{ field: "claimant_name", order: "ASC" }}
          loading={loading}
          empty={
            <ListEmptyState resource={ResourceName.CLAIMANTS} subtitle="Adjust your filters or create a new claimant" />
          }
        >
          <StyledDatagridConfigurable
            bulkActionButtons={<ClaimantsBulkActionButtons />}
            omit={["claimant_relationship", "work_status"]}
          >
            <TextField {...getProps("claimant_name", ResourceName.CLAIMANTS)} />

            <ReferenceField
              {...getProps("client_id_ref", ResourceName.CLAIMANTS)}
              reference={ResourceName.CLIENTS}
              sortable={false}
            >
              <TextField source="client_legal_name" />
            </ReferenceField>

            <FunctionField
              label="Policy"
              sortable={false}
              render={(record: Claimant) => <PolicyFieldFromClaimant policies={record.policies} />}
            />

            <FunctionField label="Claim Count" sortable={false} render={() => <ClaimCountField />} />

            <WrapperField label="Total Paid" textAlign="right" sortable={false}>
              <TotalPaidCell />
            </WrapperField>

            <WrapperField label="Specific Deductible" textAlign="right" sortable={false}>
              <SpecificDeductibleCell />
            </WrapperField>

            <WrapperField label="Aggregate Deductible" textAlign="right" sortable={false}>
              <AggregateDeductibleCell />
            </WrapperField>

            <WrapperField label="Over Deductible" textAlign="right" sortable={false}>
              <SpendOverDeductibleCell />
            </WrapperField>

            <WrapperField label="Open Tasks" textAlign="right" sortable={false}>
              <OpenTasksCell />
            </WrapperField>

            {/* Hidden by default — available via column selector */}
            <TextField source="claimant_relationship" label="Relationship" />

            <FunctionField
              source="work_status"
              label="Work Status"
              sortable={false}
              render={() => <WorkStatusCell />}
            />
          </StyledDatagridConfigurable>
        </List>
      </ListContextProvider>
    </>
  );
};

/** Transform claimant form values before POST.
 *  - Strips internal fields (_laser_data, _contract_laser_data, _lasered_conditions, _selected_policy_id)
 *  - Injects policy_data association when a policy is selected */
export const transformClaimantCreate = (data: ValidAny) => {
  const { _laser_data, _contract_laser_data, _lasered_conditions, _selected_policy_id, ...claimantData } =
    data as ClaimantCreate & {
      _laser_data?: unknown;
      _contract_laser_data?: unknown;
      _lasered_conditions?: unknown;
      _selected_policy_id?: string;
    };

  // If a policy was selected, include policy_data in the create payload so the
  // backend associates the claimant with that policy in a single request.
  if (_selected_policy_id) {
    (claimantData as Record<string, ValidAny>).policy_data = { [_selected_policy_id]: {} };
  }

  return claimantData;
};

/** After claimant creation, create the lasered-claimant entry if laser data was provided.
 * The policy association itself is handled by transformClaimantCreate (included in the POST).
 * Laser failures are non-blocking — the claimant is already persisted. A failed standing
 * deductible skips the conditional lasers, as policy create does (!4880 thread 30681):
 * without it the claimant would sit at the policy base between triggers. */
export const handleClaimantCreateSuccess =
  (dataProvider: ReturnType<typeof useDataProvider>, notify: ReturnType<typeof useNotify>) =>
  async (newRecord: ValidAny, formData: ValidAny) => {
    const laserData = (formData as Record<string, ValidAny>)?._laser_data;
    const conditionData = (formData as Record<string, ValidAny>)?._lasered_conditions as
      | { policy_id: string; conditions: LaseredConditionRequest[] }
      | undefined;
    const hasConditions = !!conditionData?.policy_id && !!conditionData.conditions?.length;
    let laserFailed = false;
    if (laserData?.policy_id && laserData?.laser_type) {
      try {
        await dataProvider.create(`policies/${laserData.policy_id}/lasered-claimants`, {
          data: {
            claimant_id_ref: newRecord.id,
            laser_type: laserData.laser_type,
            laser_reason: laserData.laser_reason,
            amt_lasered_deductible: laserData.amt_lasered_deductible,
            amt_incremental_lasered_deductible: laserData.amt_incremental_lasered_deductible,
            ...(laserData.effective_date && { effective_date: laserData.effective_date }),
          },
        });
      } catch (err) {
        console.error("Failed to laser claimant:", err);
        laserFailed = true;
        notify(
          hasConditions
            ? "Claimant created, but its lasered specific deductible could not be applied, so its conditional lasers were not added either. Add them on the policy's Lasered Claimants tab."
            : "Claimant created, but laser deductible could not be applied. Please add it manually.",
          { type: "warning" }
        );
      }
    }

    // Contract laser / full exclusion (Feature #5287). The endpoint sits below
    // the standard CRUD surface, so we PUT it directly via httpClient (mirrors
    // useContractLaser). Non-blocking — the claimant is already persisted.
    const contractData = (formData as Record<string, ValidAny>)?._contract_laser_data;
    if (contractData?.policy_id) {
      const body: ContractLaserRequest = {
        is_contract_lasered: !!contractData.is_contract_lasered,
        is_contract_excluded: !!contractData.is_contract_excluded,
        ...(contractData.is_contract_excluded
          ? {}
          : {
              amt_contract_lasered_deductible: contractData.amt_contract_lasered_deductible,
              contract_incurred_start_date: contractData.contract_incurred_start_date,
              contract_incurred_end_date: contractData.contract_incurred_end_date,
              contract_paid_start_date: contractData.contract_paid_start_date,
              contract_paid_end_date: contractData.contract_paid_end_date,
              excluded_procedures: contractData.excluded_procedures,
            }),
      };
      try {
        const { json } = await httpClient(
          `${getPartnerAwareApiUrl()}/policies/${contractData.policy_id}/lasered-claimants/${newRecord.id}/contract-laser`,
          { method: "PUT", body: JSON.stringify(body) }
        );
        if (json?.warning) {
          notify(json.warning, { type: "info" });
        }
      } catch (err) {
        console.error("Failed to apply contract laser:", err);
        notify("Claimant created, but the contract laser could not be applied. Please add it manually.", {
          type: "warning",
        });
      }
    }

    // Conditional lasers (Feature #3502, #7344). Written after the straight or
    // contract laser so the API sees the claimant's contract tiers. Each row is
    // independent; failures are reported together and are non-blocking.
    if (hasConditions && conditionData && !laserFailed) {
      const failed: string[] = [];
      const warnings = new Set<string>();
      for (const c of conditionData.conditions) {
        try {
          const saved = await laseredConditionApi.create(conditionData.policy_id, String(newRecord.id), c);
          if (saved.warning) warnings.add(saved.warning);
        } catch (err) {
          console.error("Failed to add conditional laser:", err);
          failed.push(c.lasered_condition_name);
        }
      }
      warnings.forEach((w) => notify(w, { type: "info" }));
      if (failed.length) {
        notify(
          `Claimant created, but ${failed.length === 1 ? "a conditional laser" : `${failed.length} conditional lasers`} could not be saved (${failed.join(", ")}). Add them on the policy's Lasered Claimants tab.`,
          { type: "warning" }
        );
      }
    }
  };

const ClaimantsListActions = ({ loading }: { loading?: boolean }) => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openFamilyDialog, setOpenFamilyDialog] = useState(false);
  const { canCreate } = useResourcePermissions(ResourceName.CLAIMANTS);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Don't render anything if user can't create
  if (!canCreate) {
    return (
      <TopToolbar>
        <SelectColumnsButton disabled={loading} />
        <ChunkedExportButton disabled={loading} />
      </TopToolbar>
    );
  }

  return (
    <TopToolbar>
      <CreateButton
        onClick={(e) => {
          e.preventDefault();
          setOpenCreateDialog(true);
        }}
        disabled={loading}
      />
      <Button size="small" startIcon={<GroupAddIcon />} onClick={() => setOpenFamilyDialog(true)} disabled={loading}>
        Create Family
      </Button>
      <SelectColumnsButton disabled={loading} />
      <ChunkedExportButton disabled={loading} />
      <CreateInDialog<ClaimantCreate>
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        resource={ResourceName.CLAIMANTS}
        transform={transformClaimantCreate}
        onSuccess={handleClaimantCreateSuccess(dataProvider, notify)}
      />
      <ManageFamilyDialog
        open={openFamilyDialog}
        onClose={() => setOpenFamilyDialog(false)}
        familyData={null}
        onMutationSuccess={() => {}}
      />
    </TopToolbar>
  );
};

const ClaimantsBulkActionButtons = () => {
  const { canDelete } = useResourcePermissions(ResourceName.CLAIMANTS);

  return (
    <>
      <BulkExportButton />
      {isNonProductionEnvironment() && (
        <BulkExportButton
          label="Export as JSON"
          icon={<DownloadIcon />}
          exporter={(records) => jsonExporter(records, ResourceName.CLAIMANTS)}
        />
      )}
      {/* #7591: exactly two claimants on one client; gates itself on the pair and
          merge capabilities and on a read-only partner. */}
      <MergeSelectedClaimantsButton />
      {canDelete && <BulkDeleteButton />}
    </>
  );
};
